/* eslint-disable react/react-in-jsx-scope */
import { extendOwn, defaults, map, isEmpty, filter, first } from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import { FeedSyntaxView, DefaultTextEditorView } from '@bingads-webui-campaign-react/feed-and-attribute-selector';

import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { parseRule, parseAdCustomizerFeedsRuleForSpecialChracter } from './parser';
import { queryFeedAttributeSampleValue } from './query-feed-attribute-sample-value';
import { queryFeedsByName } from './query-feeds-by-name';

const escapeFeedName = str => (str ? str.replace(/\./g, '\\.') : '');
const escapeAttrbuteName = str => (str ? str.replace(/:/g, '\\:') : '');

/** Feeds analyzer, the most complicated analyzer
 */
export class AdCustomizerFeedAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({
    deps: {
      config,
      instrumentation,
      i18n,
      jsDataCampaign,
      edmCampaign,
    },
    controller,
  }) {
    super({
      name: 'adCustomizer',
      config,
      priority: 100,
      instrumentation,
      i18n,
      activityName: 'feeds',
      controller,
    });

    this.jsDataCampaign = jsDataCampaign;
    this.edmCampaign = edmCampaign;
  }

  /*
   * To generate Ad customizer root static menu item
   */
  getMenuItem({
    startIndex, rule, target, updateAndCloseCallback,
  }) {
    return {
      name: this.i18n.getString('Picker_AdCustomizer_Label'),
      description: this.i18n.getString('Picker_AdCustomizer_Description'),
      onClick: (event) => {
        const {
          defaultText, feed, attribute, source,
        } = rule;

        const { newValue, cursorPosition } = this.formatAdCustomizerFeed({
          feedName: feed,
          attributeName: attribute,
          defaultText,
          value: target.value,
          startIndex,
          buildCompleteExpression: false,
          source,
        });
        updateAndCloseCallback({
          newValue,
          cursorPosition,
          event,
          isSelectedByUser: true,
        });
      },
    };
  }

  formatAdCustomizerFeed({
    feedName,
    attributeName,
    defaultText,
    value,
    startIndex,
    buildCompleteExpression = false,
    source,
  }) {
    const trimmedDefaultText = defaultText ? defaultText.trim() : '';
    const endIndex = startIndex + source.length;

    const prefix = value.substring(0, startIndex);
    const suffix = value.substring(endIndex);
    const feedNamePart = escapeFeedName(feedName);
    const expressionStart = '{=';
    const selectedAttributeNamePart = attributeName ? `.${escapeAttrbuteName(attributeName)}` : '';
    const defaultTextSelectedPart = trimmedDefaultText ? `:${trimmedDefaultText}` : '';
    const newValue = `${prefix}${expressionStart}${feedNamePart}${selectedAttributeNamePart}${defaultTextSelectedPart}}${suffix}`;

    const cursorPosition = buildCompleteExpression ?
      newValue.length - suffix.length :
      newValue.length - suffix.length - 1;

    return {
      newValue,
      cursorPosition,
    };
  }

  updateTargetOnUserSelectionUpdate({
    feedName, attributeName, defaultText, target, startIndex,
    updateAndCloseCallback, event, source,
  }) {
    const { newValue, cursorPosition } = this.formatAdCustomizerFeed({
      feedName,
      attributeName,
      defaultText,
      value: target.value,
      startIndex,
      buildCompleteExpression: false,
      source,
    });

    updateAndCloseCallback({
      newValue, cursorPosition, isSelectedByUser: true, event,
    });
  }

  getStartIndex(value, cursorIndex) {
    return value.lastIndexOf('{', cursorIndex);
  }

  evaluateRule(text) {
    const processedText = text || '';
    const parseResult = parseRule({
      needFullRule: true,
      isSelectedByUser: false,
      processedText,
      startIndex: this.getStartIndex(processedText, processedText.length),
      selectionEnd: processedText.length,
      config: this.config,
    });

    if (parseResult) {
      const {
        feed, attribute, startIndex, endIndex,
      } = parseResult;

      if (feed && attribute) {
        return queryFeedAttributeSampleValue({
          feedName: feed,
          attributeName: attribute,
          edmCampaign: this.edmCampaign,
          jsDataCampaign: this.jsDataCampaign,
        }).then(sample => (sample ?
          text.replace(text.substring(startIndex, endIndex), sample) :
          text));
      }
    }

    return Promise.resolve(text);
  }

  /** analyze input
  * the rule is that
  *   --> when there is {= and the { is before the cursor, then
  *     1 first parse the rule, see whether it has feed/attribute/defaultText
  *     2 if there is no feed in rule, return all the feeds got for the customer
  *     3 if there is feed in rule, but no attribute, fetch feeds (with name filter)
  *     and analyze result count
  *        .only one the feed already have full name, return attributes under it
  *        .more than one or feed name not full in one case, return feeds to display
  *        .no feed found, then return empty itemsToDisplay but defaultHandlerForInput to true
  *     4 if there is attribute in rule, but no default text, fetch specific feed
  *       and filter attributes by name, and analyze result count
  *         .only one attribute and attribute name already full, return specificAttributeName true
  *           (which means will show default text editor)
  *         .more than one or attribute not full, return attributes to display
  *         .no feed found, then return empty itemsToDisplay but defaultHandlerForInput to true
  *   --> else when no {= but { is the last character before cursor, return menu item
  *   --> else return null
  *
  * @param {Object} options - options for parametr
  * @param {String} options.text - the whole contents of the Ads text field
  * @param {Number} options.cursorIndex - the cursor position within the options.text
  * @param {Object} options.target - the input HTML element
  * @param {func} options.updateAndCloseCallback - update call back
  * @param {bool} options.isSelectedByUser - whether this is selected option
  * @returns {Object} promise which will resolve the result
  */
  analyzeInput({
    text, cursorIndex, isSelectedByUser, target, updateAndCloseCallback,
  }) {
    const processedText = text || '';
    const startIndex = this.getStartIndex(processedText, cursorIndex);

    // If cursor is right infornt of {= we don't exit here
    // when not contains {, see whether ends with {, if so, then show the menu item
    if (startIndex === -1 || startIndex === cursorIndex) {
      return Promise.resolve(null);
    }

    const rule = parseRule({
      needFullRule: false,
      isSelectedByUser,
      processedText,
      selectionEnd: cursorIndex,
      startIndex,
    });

    // The parser should have found an exression on the left from
    // the cursor AND cursor should be within the matched part of the string
    if (rule && cursorIndex <= startIndex + rule.source.length &&
      // AND cursor shouldn't be at the expression's closing "}"
      !(
        rule.isComplete &&
        cursorIndex === startIndex + rule.source.length
      )
    ) {
      const {
        feed, attribute,
      } = rule;

      extendOwn(rule, {
        type: this.name,
        defaultHandlerForInput: true,
        itemsToDisplay: [], // items have empty son so that be picked
      });

      if (attribute && isSelectedByUser) {
        rule.specificAttributeName = true;
      } else if (feed && isSelectedByUser) {
        rule.specificFeedName = true;
      }

      if (
        !rule.specificAttributeName &&
        cursorIndex > startIndex + 1 &&
        (!isEmpty(feed) || cursorIndex === startIndex + 2)
      ) {
        return queryFeedsByName({
          namePrefix: feed,
          useExactMatch: rule.specificFeedName,
          jsDataCampaign: this.jsDataCampaign,
        }).then((results) => {
          // feed did not exist, directly return, it will be backup solution
          if (!isEmpty(results)) {
            // when user already selected feed name
            if (rule.specificFeedName) {
              const attributeLowerCased = attribute.toLowerCase();
              const attributesToDisplay = isEmpty(attribute) ?
                results[0].AttributeNames :
                filter(
                  results[0].AttributeNames,
                  name => name.toLowerCase().indexOf(attributeLowerCased) === 0
                );

              // show picker when there are attributes and user input attribute has not ended
              if (!isEmpty(attributesToDisplay) &&
                ((attributesToDisplay.length > 1) ||
                  (attributesToDisplay[0].length > attribute.length))) {
                rule.itemsToDisplay = map(attributesToDisplay, name => ({
                  name,
                  description: '',
                  onClick: event => this.updateTargetOnUserSelectionUpdate({
                    feedName: feed,
                    attributeName: name,
                    defaultText: '',
                    updateAndCloseCallback,
                    target,
                    startIndex,
                    event,
                    source: rule.source,
                  }),
                }));
              } else if (!isEmpty(attributesToDisplay) && (attributesToDisplay.length === 1)) {
                // when only one attribute and it is same length as input, show default text
                // and also provide the top-level menu item in case there is ambiguity in which
                // analyzer to use
                rule.match = true;
                rule.specificAttributeName = true;
                rule.itemsToDisplay = [this.getMenuItem({
                  startIndex,
                  rule,
                  target,
                  updateAndCloseCallback,
                })];
                return Promise.resolve(rule);
              }
            } else {
              // when no feed name specified
              rule.itemsToDisplay = map(results, ({ Name, AttributeNames }) => {
                const processAttributeNames = first(AttributeNames, 3);

                if (AttributeNames.length > 3) {
                  processAttributeNames.push('...');
                }
                const attributeDescriptionPre = this.i18n.getString('Feeds_FeedItemsPage_Attributes_Description');
                return {
                  name: Name,
                  description: `${attributeDescriptionPre}${processAttributeNames.join(', ')}`,
                  onClick: event => this.updateTargetOnUserSelectionUpdate({
                    feedName: Name,
                    attributeName: '',
                    defaultText: '',
                    updateAndCloseCallback,
                    event,
                    target,
                    startIndex,
                    source: rule.source,
                  }),
                };
              });
            }
          }

          rule.match = !isEmpty(rule.itemsToDisplay);
          return Promise.resolve(rule);
        });
      }

      // We get here if feed name is empty and we are not at position two characters
      // past startIndex.
      // Show menu only if we are not past two characters after startIndex, exclusive
      if (rule.specificAttributeName || cursorIndex < startIndex + 2) {
        return Promise.resolve(defaults({
          itemsToDisplay: [this.getMenuItem({
            startIndex,
            rule,
            target,
            updateAndCloseCallback,
          })],
          type: this.name,
          match: rule.specificAttributeName,
        }, rule));
      }
    }

    return Promise.resolve(null);
  }

  /* return a reactjs object here */
  getSyntax(analyzeResult) {
    const { specificAttributeName, specificFeedName } = analyzeResult || {};

    return (<FeedSyntaxView
      i18n={this.i18n}
      selectedFeed={!!specificFeedName}
      selectedAttribute={!!specificAttributeName}
    />);
  }

  needEditor(analyzeResult) {
    const { specificAttributeName } = analyzeResult || {};
    return specificAttributeName;
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback, remainingCountCallBack,
  }) {
    const {
      defaultText, feed, attribute, startIndex, source,
    } = analyzeResult;
    let maxLength;
    if (remainingCountCallBack) {
      maxLength = remainingCountCallBack() + (defaultText ? defaultText.length : 1);
    }
    return (<DefaultTextEditorView
      i18n={this.i18n}
      maxLength={maxLength}
      defaultText={defaultText}
      onSaveOrOnCancel={(newDefaultText, event) => {
        const { newValue, cursorPosition } = this.formatAdCustomizerFeed({
          feedName: feed,
          attributeName: attribute,
          defaultText: newDefaultText,
          value: target.value,
          startIndex,
          buildCompleteExpression: true,
          source,
        });
        updateAndCloseCallback({
          newValue,
          cursorPosition,
          event,
          isSelectedByUser: true,
        });
      }}
    />);
  }

  getCharCountExcludingAdCustomizers(text, isComplete) {
    let processedText = text;
    let charCount = 0;
    const rule = parseAdCustomizerFeedsRuleForSpecialChracter(processedText, 0);
    if (rule && rule.source && (!isComplete || rule.isComplete)) {
      if (rule.isComplete) {
        charCount += rule.defaultText ? rule.defaultText.length : 1;
      } else {
        charCount += rule.source.length;
      }
      processedText = processedText.replace(rule.source, '');
    }

    return {
      processedText,
      charCount,
    };
  }
}
